import { BaseResource } from './base';
import { Teacher, ListResponse, PaginationParams } from '../types';

export interface CreateTeacherData {
    firstName: string;
    lastName: string;
    email: string;
    department: string;
    subjects: string[];
    hireDate: string;
    qualifications: string[];
    address?: {
        street: string;
        city: string;
        state: string;
        zipCode: string;
        country?: string;
    };
    phone?: string;
}

export interface UpdateTeacherData extends Partial<CreateTeacherData> {
    status?: 'active' | 'inactive';
}

export interface TeacherFilters extends PaginationParams {
    department?: string;
    status?: 'active' | 'inactive';
    search?: string;
    subject?: string;
    hireDateFrom?: string;
    hireDateTo?: string;
}

export class TeachersResource extends BaseResource {
    /**
     * List all teachers with optional filtering and pagination
     */
    async list(filters?: TeacherFilters): Promise<ListResponse<Teacher>> {
        return super.requestGet<ListResponse<Teacher>>('/teachers', filters);
    }

    /**
     * Get a specific teacher by ID
     */
    async get(teacherId: string): Promise<Teacher> {
        return super.requestGet<Teacher>(`/teachers/${teacherId}`);
    }

    /**
     * Create a new teacher
     */
    async create(data: CreateTeacherData): Promise<Teacher> {
        return super.requestPost<Teacher>('/teachers', data);
    }

    /**
     * Update an existing teacher
     */
    async update(teacherId: string, data: UpdateTeacherData): Promise<Teacher> {
        return super.requestPatch<Teacher>(`/teachers/${teacherId}`, data);
    }

    /**
     * Delete a teacher
     */
    async delete(teacherId: string): Promise<{ success: boolean; message: string }> {
        return super.requestDelete(`/teachers/${teacherId}`);
    }

    /**
     * Get teacher's courses
     */
    async getCourses(teacherId: string, filters?: PaginationParams & { semester?: string }): Promise<ListResponse<any>> {
        return super.requestGet<ListResponse<any>>(`/teachers/${teacherId}/courses`, filters);
    }

    /**
     * Get teacher's schedule
     */
    async getSchedule(teacherId: string, filters?: { semester?: string; week?: string }): Promise<any> {
        return super.requestGet(`/teachers/${teacherId}/schedule`, filters);
    }

    /**
     * Search teachers by name, email, or department
     */
    async search(query: string, filters?: Omit<TeacherFilters, 'search'>): Promise<ListResponse<Teacher>> {
        return super.requestGet<ListResponse<Teacher>>('/teachers/search', { q: query, ...filters });
    }
}
